import LogoComponent from "../../components/Logos/LogoComponent";
import imageIcon from "../../assets/imageIcon.png";
import ActionButton from "../../components/buttons/ActionButton";
import { useNavigate } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from "react";
import { CurrentUserContext } from "../../App";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3080";

// Pre-made avatar options
const PREMADE_AVATARS = [
  { id: 1, url: "/avatars/avatar1.svg", label: "Bear" },
  { id: 2, url: "/avatars/avatar2.svg", label: "Cat" },
  { id: 3, url: "/avatars/avatar3.svg", label: "Dog" },
  { id: 4, url: "/avatars/avatar4.svg", label: "Fox" },
  { id: 5, url: "/avatars/avatar5.svg", label: "Owl" },
  { id: 6, url: "/avatars/avatar6.svg", label: "Panda" },
  { id: 7, url: "/avatars/avatar7.svg", label: "Penguin" },
  { id: 8, url: "/avatars/avatar8.svg", label: "Rabbit" },
  { id: 9, url: "/avatars/avatar9.svg", label: "Wolf" },
];

export default function SelectAvatarPage() {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [customFile, setCustomFile] = useState(null);
  const [customPreview, setCustomPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");
  const uploadFileRef = useRef(null);
  const carouselRef = useRef(null);
  const navigate = useNavigate();
  const { currentUser, setCurrentUser } = useContext(CurrentUserContext);

  // When a custom file is selected, clear premade selection and create preview
  useEffect(() => {
    if (!customFile) {
      setCustomPreview(null);
      return;
    }
    const objectUrl = URL.createObjectURL(customFile);
    setCustomPreview(objectUrl);
    setSelectedIndex(-1); // deselect premade
    return () => URL.revokeObjectURL(objectUrl);
  }, [customFile]);

  const handleFileChange = (e) => {
    if (!e.target.files || e.target.files.length === 0) {
      setCustomFile(null);
      return;
    }
    const file = e.target.files[0];
    const extension = file.name.split(".").pop().toLowerCase();
    const validExtensions = ["jpg", "png", "jpeg"];
    if (validExtensions.includes(extension)) {
      setCustomFile(file);
    } else {
      alert("You must select a JPG, JPEG, or PNG file.");
    }
  };

  const selectPremade = (index) => {
    setSelectedIndex(index);
    setCustomFile(null);
    setCustomPreview(null);
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const nextPage = async () => {
    setError("");
    setIsUploading(true);

    try {
      let body;
      if (customFile) {
        const imageBase64 = await fileToBase64(customFile);
        body = { imageBase64 };
      } else if (selectedIndex >= 0) {
        body = { avatarUrl: PREMADE_AVATARS[selectedIndex].url };
      } else {
        // No selection — skip avatar
        navigate("/login/newusername");
        return;
      }

      const response = await fetch(`${API_URL}/uploadAvatar`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${currentUser.token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (response.ok && data.message === "success") {
        // Update context and localStorage with new avatar
        const updatedUser = { ...currentUser, avatar: data.avatar };
        setCurrentUser(updatedUser);
        localStorage.setItem("currentUser", JSON.stringify(updatedUser));
        navigate("/login/newusername");
      } else {
        setError(data.message || "Failed to upload avatar.");
      }
    } catch (err) {
      console.error("Avatar upload error:", err);
      setError("Failed to upload avatar. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  // Determine the main displayed avatar
  const mainAvatarSrc = customPreview
    ? customPreview
    : selectedIndex >= 0
    ? PREMADE_AVATARS[selectedIndex].url
    : PREMADE_AVATARS[0].url;

  // Build the visible carousel items (show selected in center with neighbors)
  const getVisibleAvatars = () => {
    if (selectedIndex < 0) return PREMADE_AVATARS.slice(0, 5);
    const total = PREMADE_AVATARS.length;
    const items = [];
    for (let offset = -2; offset <= 2; offset++) {
      const idx = ((selectedIndex + offset) % total + total) % total;
      items.push({ ...PREMADE_AVATARS[idx], originalIndex: idx, offset });
    }
    return items;
  };

  return (
    <div className="d-flex flex-column min-vh-100 justify-content-center align-items-center">
      <div
        className="d-flex flex-column align-items-center justify-content-center"
        style={{ maxWidth: "30rem", width: "100%" }}
      >
        {/* Logo */}
        <div className="mb-4">
          <LogoComponent />
        </div>

        {/* Title */}
        <div className="mb-4">
          <span
            className="main-purple fw-bold"
            style={{ fontSize: "2.4rem" }}
          >
            Select your Avatar
          </span>
        </div>

        {/* Main avatar display */}
        <div className="mb-3 d-flex justify-content-center">
          <div
            style={{
              width: "180px",
              height: "180px",
              borderRadius: "50%",
              overflow: "hidden",
              border: "3px solid #7c3aed",
              backgroundColor: "#2a2a3e",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={mainAvatarSrc}
              alt="Selected Avatar"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </div>
        </div>

        {/* Carousel of premade avatars */}
        {!customPreview && (
          <div
            ref={carouselRef}
            className="d-flex align-items-center justify-content-center gap-2 mb-3"
            style={{
              width: "100%",
              overflowX: "auto",
              padding: "8px 0",
            }}
          >
            {getVisibleAvatars().map((item) => {
              const isCenter = item.offset === 0;
              const size = isCenter ? 70 : Math.abs(item.offset) === 1 ? 55 : 42;
              const opacity = isCenter ? 1 : Math.abs(item.offset) === 1 ? 0.7 : 0.45;
              return (
                <div
                  key={item.id}
                  onClick={() => selectPremade(item.originalIndex)}
                  style={{
                    width: `${size}px`,
                    height: `${size}px`,
                    minWidth: `${size}px`,
                    borderRadius: "50%",
                    overflow: "hidden",
                    cursor: "pointer",
                    border: isCenter ? "2px solid #7c3aed" : "2px solid transparent",
                    opacity,
                    transition: "all 0.2s ease",
                    backgroundColor: "#2a2a3e",
                  }}
                >
                  <img
                    src={item.url}
                    alt={item.label}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* Navigation arrows for carousel */}
        {!customPreview && (
          <div className="d-flex gap-3 mb-3">
            <button
              type="button"
              className="btn btn-sm btn-outline-light rounded-circle"
              style={{ width: "36px", height: "36px", padding: 0 }}
              onClick={() =>
                selectPremade(
                  (selectedIndex - 1 + PREMADE_AVATARS.length) % PREMADE_AVATARS.length
                )
              }
            >
              &lt;
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-light rounded-circle"
              style={{ width: "36px", height: "36px", padding: 0 }}
              onClick={() =>
                selectPremade((selectedIndex + 1) % PREMADE_AVATARS.length)
              }
            >
              &gt;
            </button>
          </div>
        )}

        {/* Add image from gallery */}
        <div
          onClick={() => uploadFileRef?.current?.click()}
          style={{ cursor: "pointer" }}
          className="d-flex justify-content-center align-items-center gap-2 text-center mb-4"
        >
          <input
            hidden
            type="file"
            accept=".png, .jpg, .jpeg"
            ref={uploadFileRef}
            onChange={handleFileChange}
          />
          <img src={imageIcon} alt="Icon" />
          <span>{customFile ? customFile.name : "Add image from gallery"}</span>
        </div>

        {/* Error message */}
        {error && (
          <div className="text-danger mb-3 text-center">
            {error}
          </div>
        )}

        {/* Continue button */}
        <div className="d-flex flex-column gap-2 p-2 w-100">
          <ActionButton
            buttonAction={nextPage}
            buttonText={isUploading ? "Uploading..." : "Continue"}
            disabled={isUploading}
          />
        </div>
      </div>
    </div>
  );
}
